import React, { useCallback, useState, useEffect } from 'react'
import ZionForm, { useZionForm, FormTextField } from '@fs/zion-form'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  InfoSheetPanel,
  Expander,
  ExpanderHeader,
  ExpanderContent,
  Separator,
  Paragraph,
  LabelTooltip,
  InlineButton,
  H6,
  Subheading,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import zionDebug from '@fs/zion-debug'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import cloneDeep from 'lodash.clonedeep'
import { trackEvent } from '@fs/zion-analytics'
import {
  createFieldValue,
  createHighlightsDispatches,
  generateSls,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import { useR2StateContext, useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import PanelHeader from './PanelHeader'
import EditDanglingFieldFooter from './EditDanglingFieldFooter'
import { headers, R2AppName } from '../../../../../shared/services/config'
import { errorCollector } from '../../../../../shared/utils/utils'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSetFocusedIndex } from '../../../../components/r2imageBrowser/hooks'
import HighlightButtonOld from '../../../../components/generic/panels/shared/HighlightButtonOld'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const debug = zionDebug('rip:R2:EditDanglingField')

const EditDanglingField = () => {
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const [t] = useTranslation()
  const [saving, setSaving] = useState()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])
  const [reducerActions, setReducerActions] = useState([])
  const [saveError, setSaveError] = useState()
  const dispatch = useSlsDispatchContext()
  const r2Dispatch = useR2DispatchContext()
  const setFocusedIndex = useSetFocusedIndex()
  const { closeOverlay, editDanglingFieldOverlay } = useOverlayContext()

  const shape = {}
  const schema = Yup.object().shape(shape)
  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {},
  })
  const field = getNestedOrFloatingElementById(state.images, r2State?.activeFieldId)
  const lastFieldValue = getMostRecentFieldValue(field)
  const fieldDisplayLabel = field?.typeData?.displayLabel
  const heading = fieldDisplayLabel
    ? t('edit-dangling-field.heading', '{fieldName}: {fieldValue}', {
        fieldName: fieldDisplayLabel,
        fieldValue: lastFieldValue,
      })
    : ''
  const { handleFieldChange, handleOnFocus } = { handleFieldChange: () => {}, handleOnFocus: () => {} }

  const { reset } = methods
  useEffect(() => {
    const formDefaultValues = {}
    if (field) {
      formDefaultValues[field.id] = lastFieldValue
    }
    reset(formDefaultValues)
  }, [field, lastFieldValue, reset])

  const instructions = t('edit-dangling-field.instructions.heading', 'Fix any mistakes that you see.')

  const goBackToEditEventsPanel = useCallback(() => {
    editDanglingFieldOverlay.handleClick()
    setFocusedIndex(r2State.previousIndex, true)
    closeOverlay('editDanglingField')
  }, [editDanglingFieldOverlay, r2State.previousIndex, closeOverlay, setFocusedIndex])

  useEffect(() => {
    if (reducerActions?.length > 0) {
      const addFvAction = reducerActions[0]
      const updateAllImages = []
      state.images.forEach((img) => {
        const newAction = cloneDeep(addFvAction)
        newAction.value.partitionKey = img.partitionKey
        updateAllImages.push(newAction)
      })
      dispatch({
        type: 'BATCH_DISPATCHES',
        value: updateAllImages,
      })
      setReducerActions([])
      goBackToEditEventsPanel()
    }
  }, [
    reducerActions,
    dispatch,
    editDanglingFieldOverlay,
    r2State.previousIndex,
    r2Dispatch,
    goBackToEditEventsPanel,
    state.images,
  ])

  const saveSls = useCallback(
    (slsActions, dispatches) => {
      if (saveChangesFlag.isOn) {
        saveSLSActions({
          actions: slsActions,
          userId: r2State.userId,
          appName: R2AppName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          splunkDebugModeArguments: slsActionsSplunkDebugger.isOn
            ? { theFileFromWhichSaveWasCalled: 'EditDanglingField' }
            : {},
        })
          .then(() => {
            setReducerActions(dispatches)
          })
          .catch((err) => {
            setSaveError(err)
            errorCollector(err, state.currentImageArk, {
              codeId: 75,
              slsActions,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          })
          .finally(() => {
            setSaving(false)
          })
      } else {
        debug('Saving changes to SLS is disabled', dispatches, slsActions)
        setSaving(false)
      }
    },
    [saveChangesFlag.isOn, slsActionsSplunkDebugger.isOn, r2State.userId, state.currentImageArk]
  )

  const handleSubmit = useCallback(
    (values) => {
      trackEvent({
        event_name: 'dangling_field_edit',
        link_name: `Edit: Dangling Field: Save`,
      })
      const attribution = { comment: '', reasonId: '', app: R2AppName }
      let dispatches = [
        {
          type: 'ADD_FV',
          value: {
            partitionKey: field.partitionKey,
            id: field.id,
            fieldValue: createFieldValue(values[field.id], attribution),
          },
        },
      ]
      dispatches = dispatches
        .concat(createHighlightsDispatches(state.images, state.currentImageArk, r2State.editHighlights))
        .concat(createHighlightsDispatches(state.images, state.currentImageArk, r2State.referenceEditHighlights))
      setSaving(true)
      const slsActions = generateSls(dispatches)
      debug('Save data: ', { dispatches, slsActions })
      saveSls(slsActions, dispatches)
    },
    [
      field?.id,
      field?.partitionKey,
      r2State.editHighlights,
      saveSls,
      r2State.referenceEditHighlights,
      state.images,
      state.currentImageArk,
    ]
  )

  debug('Render', { field, r2State })

  return (
    <>
      <InfoSheetPanel
        title={t('dangling-field.title', 'Edit {fieldName}', { fieldName: fieldDisplayLabel })}
        headingColor="red"
        onClose={goBackToEditEventsPanel}
        {...editDanglingFieldOverlay}
        footer={<EditDanglingFieldFooter saving={saving} goBackToEditEventsPanel={goBackToEditEventsPanel} />}
      >
        <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-dangling-field">
          <PanelHeader headingSize="H5" heading={heading} caption={instructions} />
          <H6>
            {t('edit-dangling-field.edit-field.heading', '1. Edit the {fieldName} field.', {
              fieldName: fieldDisplayLabel,
            })}
          </H6>
          {field && (
            <FormTextField
              data-testid={field?.fieldType || field?.elementType}
              name={field.id}
              key={field.id}
              label={field.label}
              placeholder={field.label}
              maxLength={256}
              onFocus={() => {
                handleOnFocus({ element: field, r2Dispatch })
              }}
              onBlur={() => handleFieldChange(r2Dispatch)}
            />
          )}
          <LabelTooltip
            label={t(
              'edit-dangling-field.different-page.help-tooltip-label',
              'Someone found this information on page {currentPage}. You came from page {previousPage}.',
              { currentPage: sharedBrowserState.focusedIndex + 1, previousPage: r2State.previousIndex + 1 }
            )}
            target={
              <InlineButton>
                {t('edit-dangling-field.different-page.info-button', 'Why is this page different?')}
              </InlineButton>
            }
          />
          <Separator size="xs" />
          <H6>
            {t('edit-dangling-field.edit-highlight.heading', '2. Edit the highlight.')}
            <Subheading>
              {t('edit-dangling-field.edit-highlight.sub-heading', 'Match the highlight to the transcription.')}
            </Subheading>
          </H6>
          <Expander>
            <ExpanderHeader>
              <Paragraph size="sm">{t('edit-dangling-field.edit-highlight.tips.heading', 'Highlight Tips')}</Paragraph>
            </ExpanderHeader>
            <ExpanderContent>
              <Paragraph size="sm">
                <span style={{ whiteSpace: 'pre-wrap' }}>
                  {t(
                    'edit-dangling-field.edit-highlight.tips.body',
                    '• Select and deselect highlights as needed.\n• The selected highlight will turn yellow with a checkmark.\n• The deselected highlight will turn blue.\n• Create a New Highlight if no highlight'
                  )}
                </span>
              </Paragraph>
            </ExpanderContent>
          </Expander>
          <Separator size="xs" />
          <HighlightButtonOld field={{ highlightIds: [] }} />
        </ZionForm>
      </InfoSheetPanel>

      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}

export default EditDanglingField
